import { datadogRum } from '@datadog/browser-rum';
import { getIntegrationAuthConfig } from '../integrationAuth.util';
import { FirebaseApp, FirebaseOptions, initializeApp } from 'firebase/app';
import {
  Auth,
  GoogleAuthProvider,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  getAuth,
  getIdToken,
  initializeAuth,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth';
import { JwtPayload, jwtDecode } from 'jwt-decode';
import { resetRedux } from '../../actions/sharedActions';
import { getStore } from 'redux/reemaStore.util';
import { getEnvironment } from 'toolkit/environment/environment/environment.util';

const shouldUseRedirect = (): boolean => {
  return false;
};

const REDIRECT_STORAGE_KEY = 'login-redirect-url';

export interface FirebaseSharedClientsConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  postLoginRedirect: string;
  postLogoutRedirect: string;
}

const firebaseConfigFactory = (): FirebaseSharedClientsConfig => {
  return {
    apiKey: getEnvironment().firebase.apiKey,
    authDomain: shouldUseRedirect()
      ? getEnvironment().host
      : getEnvironment().firebase.authDomain,
    projectId: getEnvironment().firebase.projectId,
    postLoginRedirect: '/docket',
    postLogoutRedirect: '/',
  };
};

export class AuthService {
  firebaseApp: FirebaseApp;
  firebaseAuth: Auth;

  _config: FirebaseSharedClientsConfig;
  get config(): FirebaseSharedClientsConfig {
    if (!this._config) {
      this._config = firebaseConfigFactory();
    }
    return this._config;
  }

  private getFirebaseApp(): FirebaseApp {
    if (this.firebaseApp) {
      return this.firebaseApp;
    }
    const firebaseConfig: FirebaseOptions = {
      apiKey: this.config.apiKey,
      authDomain: this.config.authDomain,
      projectId: this.config.projectId,
    };
    this.firebaseApp = initializeApp(firebaseConfig);
    return this.firebaseApp;
  }

  private getFirebaseAuth(): Auth {
    if (this.firebaseAuth) {
      return this.firebaseAuth;
    }
    this.firebaseAuth = initializeAuth(this.getFirebaseApp(), {
      persistence: browserLocalPersistence,
      popupRedirectResolver: browserPopupRedirectResolver,
    });
    return this.firebaseAuth;
  }

  private async initialize(): Promise<void> {
    const auth = this.getFirebaseAuth();
    const prom = new Promise(resolve => {
      onAuthStateChanged(auth, user => {
        if (user) {
          getIdToken(user).then(token => {
            const payload = jwtDecode<JwtPayload>(token);
            datadogRum.setUser({
              name: payload['name'],
              userId: payload['reemaUserId'],
              email: payload['email'],
            });
            resolve(user);
          });
        } else {
          resolve(user);
        }
      });
    });
    await prom;
  }

  private initializePromise: Promise<void> = null;

  async ensureSetup(): Promise<void> {
    if (!this.initializePromise) {
      this.initializePromise = this.initialize();
    }
    return this.initializePromise;
  }

  /**
   * Opens Google sign-in and navigates to the post-login URL.
   * @param redirectTo - Optional override; otherwise uses stored redirect or `/docket`.
   */
  async login(redirectTo?: string) {
    await this.ensureSetup();

    const redirectUrl =
      redirectTo ?? localStorage.getItem(REDIRECT_STORAGE_KEY) ?? '/docket';
    const provider = new GoogleAuthProvider();

    if (shouldUseRedirect()) {
      signInWithRedirect(this.getFirebaseAuth(), provider);
    } else {
      signInWithPopup(this.getFirebaseAuth(), provider)
        .then(() => {
          window.location.href = redirectUrl;
          localStorage.removeItem(REDIRECT_STORAGE_KEY);
        })
        .catch(error => {
          console.log('Popup error', error);
        });
    }
  }

  redirectToLogin() {
    localStorage.setItem(
      REDIRECT_STORAGE_KEY,
      window.location.pathname + window.location.search
    );
    window.location.href = '/login';
  }

  async logout(): Promise<void> {
    try {
      const store = getStore();
      await new Promise<void>(resolve => {
        setTimeout(async () => {
          try {
            await store.dispatch(
              resetRedux({ preserveImpersonatedUser: false })
            );
            resolve();
          } catch (error) {
            console.error('Error during Redux reset:', error);
            resolve();
          }
        }, 0);
      });

      await this.ensureSetup();
      await signOut(this.firebaseAuth);

      datadogRum.clearUser();
      this.redirectToLogin();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }

  /** Firebase Auth error codes that indicate transient/network failure rather than invalid session. */
  private static readonly TRANSIENT_ERROR_CODES = [
    'auth/network-request-failed',
    'auth/timeout',
  ] as const;

  async getToken(forceRefresh: boolean = false): Promise<string> {
    if (getIntegrationAuthConfig()) {
      return 'integration-auth-bypass';
    }

    await this.ensureSetup();
    const firebaseAuth = getAuth();
    try {
      let token = null;
      if (firebaseAuth.currentUser) {
        token = await getIdToken(firebaseAuth.currentUser, forceRefresh);
      }
      return token;
    } catch (e) {
      const err = e as { code?: string };
      console.log('getToken error', e);
      const isTransient =
        err.code != null &&
        (AuthService.TRANSIENT_ERROR_CODES as readonly string[]).includes(
          err.code
        );
      if (
        isTransient &&
        forceRefresh &&
        firebaseAuth.currentUser != null
      ) {
        try {
          return await getIdToken(firebaseAuth.currentUser, false);
        } catch {
          return null;
        }
      }
      return null;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    if (getIntegrationAuthConfig()) {
      return true;
    }

    await this.ensureSetup();
    const token = await this.getToken();
    if (!token) return false;

    return true;
  }
}

export const authService = new AuthService();
